
// filter-system.js
class FilterSystem {
  constructor(config) {
    this.aiGenId = config.aiGenId;
    this.availableTags = config.availableTags;
    this.currentTags = [];
    this.currentSizes = [];
    this.productCache = new Map();
    this.searchCache = new Map();
    this.allProducts = []; // Cache all products from initial grid
    this.isRefining = false; // Track if background refinement is running
    
    this.init();
  }

  init() {
    this.setupElements();
    this.setupEventListeners();
    this.renderUI();
    
    // Pre-cache all products from the initial grid
    this.cacheInitialProducts();
    
    // Load sizes from initial products
    this.loadInitialSizes();
    
    // Check for URL parameters
    this.checkUrlParams();
  }

  setupElements() {
    // Use the dynamic aiGenId in selectors
    this.styleDropdownToggle = document.querySelector('.style-dropdown__toggle');
    this.styleDropdownMenu = document.querySelector('.style-dropdown__menu');
    this.styleDropdownItems = document.querySelector('[data-style-items]');
    this.filterButton = document.querySelector('[data-filter-toggle]');
    this.filterDrawer = document.querySelector('[data-filter-drawer]');
    this.filterOverlay = document.querySelector('[data-filter-overlay]');
    this.filterClose = document.querySelector('[data-filter-close]');
    this.filterClear = document.querySelector('[data-filter-clear]');
    this.styleOptions = document.querySelector('[data-style-options]');
    this.sizeOptions = document.querySelector('[data-size-options]');
    this.activeFiltersContainer = document.querySelector('[data-active-filters]');
    
    // Use dynamic product grid selector
    this.productGridEl = document.querySelector(`product-grid-${this.aiGenId}`);
    this.gridContainer = this.productGridEl ? 
      this.productGridEl.querySelector('[data-grid-container]') : null;
    
    this.accordions = document.querySelectorAll('.filter-accordion');

    if (!this.gridContainer) {
      console.warn('Grid container not found — aborting filter script.');
      return;
    }

    // Cache original grid HTML
    this.originalGridHTML = this.gridContainer.innerHTML;
  }

  cacheInitialProducts() {
    // Extract product data from the initial grid HTML for fast client-side filtering
    const productElements = this.gridContainer.querySelectorAll(`.ai-product-card-${this.aiGenId}`);
    this.allProducts = [];
    
    productElements.forEach(card => {
      const productId = card.getAttribute('data-product-id');
      const title = card.querySelector(`.ai-product-card__title-${this.aiGenId}`)?.textContent?.trim() || '';
      const price = card.querySelector(`.ai-product-card__price-${this.aiGenId}`)?.textContent?.trim() || '';
      const handle = card.querySelector('[data-quick-add]')?.getAttribute('data-product-handle') || '';
      const image = card.querySelector('img')?.getAttribute('src') || '';
      
      if (productId && handle) {
        this.allProducts.push({
          id: productId,
          title: title,
          price: price,
          handle: handle,
          featured_image: image,
        });
      }
    });
    
    console.log(`Cached ${this.allProducts.length} initial products for fast filtering`);
  }

  async loadInitialSizes() {
    try {
      // Fetch sizes from the initial products
      const handles = this.allProducts.map(p => p.handle).slice(0, 12); // Limit to first 12 for performance
      const productsWithVariants = await this.fetchFullProductsByHandles(handles);
      const initialSizes = this.extractSizesFromProducts(productsWithVariants);
      
      console.log('Loaded initial sizes:', initialSizes);
      this.renderSizeFilterOptions(initialSizes);
    } catch (error) {
      console.error('Error loading initial sizes:', error);
      // Fallback to placeholder sizes
      const placeholderSizes = ['4', '5', '6', '7', '8', '9', '10', '11'];
      this.renderSizeFilterOptions(placeholderSizes);
    }
  }

  setupEventListeners() {

    this.filterApply = document.querySelector('[data-filter-apply]');

this.filterApply?.addEventListener('click', () => {
  this.closeFilterDrawer();
  Promise.resolve().then(() => this.handleFilterChange());
});

    if (!this.gridContainer) return;

    this.setupAccordions();

    if (this.styleDropdownToggle) {
      this.styleDropdownToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const expanded = this.styleDropdownToggle.getAttribute('aria-expanded') === 'true';
        this.styleDropdownToggle.setAttribute('aria-expanded', !expanded);
        this.styleDropdownMenu.classList.toggle('active');
      });
    }

    // Close dropdown on outside click
    document.addEventListener('click', (e) => {
      if (!this.styleDropdownToggle || !this.styleDropdownMenu) return;
      if (!this.styleDropdownToggle.contains(e.target) && !this.styleDropdownMenu.contains(e.target)) {
        this.styleDropdownToggle.setAttribute('aria-expanded', 'false');
        this.styleDropdownMenu.classList.remove('active');
      }
    });

    // Drawer toggles
    this.filterButton?.addEventListener('click', () => {
      this.filterDrawer.classList.add('active');
      document.body.style.overflow = 'hidden';
    });
    
    this.filterOverlay?.addEventListener('click', () => this.closeFilterDrawer());
    this.filterClose?.addEventListener('click', () => this.closeFilterDrawer());
    this.filterClear?.addEventListener('click', () => this.clearAllFilters());
  }

  setupAccordions() {
    this.accordions.forEach(acc => {
      const header = acc.querySelector('[data-accordion-toggle]');
      if (!header) return;
      header.addEventListener('click', () => acc.classList.toggle('active'));
    });
  }

  closeFilterDrawer() {
    this.filterDrawer.classList.remove('active');
    document.body.style.overflow = '';
  }

  // Utility functions
  normalize(s) {
    return String(s || '').trim().toLowerCase();
  }

  escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (s) => 
      ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[s]
    );
  }

  // Render functions
  renderStyleDropdownItems() {
    if (!this.styleDropdownItems) return;
    this.styleDropdownItems.innerHTML = '';
    
    this.availableTags.forEach(t => {
      const btn = document.createElement('button');
      btn.className = 'style-dropdown__item';
      btn.type = 'button';
      btn.textContent = t.title;
      btn.dataset.tag = t.tag;
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        this.currentTags = [t.tag];
        this.syncCheckboxesWithCurrentTags();
        this.updateActiveFilters();
        this.closeStyleDropdown();
        await this.handleFilterChange();
      });
      this.styleDropdownItems.appendChild(btn);
    });
  }

  closeStyleDropdown() {
    if (!this.styleDropdownToggle || !this.styleDropdownMenu) return;
    this.styleDropdownToggle.setAttribute('aria-expanded', 'false');
    this.styleDropdownMenu.classList.remove('active');
  }

  renderStyleFilterOptions() {
    if (!this.styleOptions) return;
    this.styleOptions.innerHTML = '';
    
    this.availableTags.forEach(t => {
      const opt = document.createElement('div');
      opt.className = 'filter-option';
      opt.dataset.value = t.tag;
      opt.innerHTML = `
        <div class="filter-option__checkbox" aria-hidden="true"></div>
        <span class="filter-option__label">${t.title}</span>
      `;
      opt.addEventListener('click', async (e) => {
        e.stopPropagation();
        const cb = opt.querySelector('.filter-option__checkbox');
        cb.classList.toggle('checked');
        const val = opt.dataset.value;
        
        if (cb.classList.contains('checked')) {
          if (!this.currentTags.includes(val)) this.currentTags.push(val);
        } else {
          this.currentTags = this.currentTags.filter(x => x !== val);
        }
        this.updateActiveFilters();
        await this.handleFilterChange();
      });
      this.styleOptions.appendChild(opt);
    });
  }

  renderSizeFilterOptions(sizes = []) {
    if (!this.sizeOptions) return;
    this.sizeOptions.innerHTML = '';
    
    if (sizes.length === 0) {
      // Show loading state for sizes
      this.sizeOptions.innerHTML = '<div class="filter-option"><span class="filter-option__label">Loading sizes...</span></div>';
      return;
    }
    
    sizes.forEach(sz => {
      const option = document.createElement('div');
      option.className = 'filter-option';
      option.dataset.value = sz;
      option.innerHTML = `
        <div class="filter-option__checkbox" aria-hidden="true"></div>
        <span class="filter-option__label">${sz}</span>
      `;
      option.addEventListener('click', async (e) => {
        e.stopPropagation();
        const cb = option.querySelector('.filter-option__checkbox');
        cb.classList.toggle('checked');
        const val = option.dataset.value;
        
        if (cb.classList.contains('checked')) {
          if (!this.currentSizes.includes(val)) this.currentSizes.push(val);
        } else {
          this.currentSizes = this.currentSizes.filter(s => s !== val);
        }
        this.updateActiveFilters();
        await this.handleFilterChange();
      });
      this.sizeOptions.appendChild(option);
    });
    
  }

  syncCheckboxesWithCurrentTags() {
    document.querySelectorAll('[data-filter-group="style"] .filter-option').forEach(opt => {
      const cb = opt.querySelector('.filter-option__checkbox');
      if (!cb) return;
      const v = opt.dataset.value;
      if (this.currentTags.includes(v)) cb.classList.add('checked');
      else cb.classList.remove('checked');
    });
  }

  updateActiveFilters() {
    if (!this.activeFiltersContainer) return;
    this.activeFiltersContainer.innerHTML = '';

    // Tags badges
    this.currentTags.forEach(tag => {
      const el = document.createElement('div');
      el.className = 'active-filter';
      el.innerHTML = `<span>${tag}</span><button type="button" class="active-filter__remove" data-type="tag" data-value="${tag}">✕</button>`;
      el.querySelector('button').addEventListener('click', async (e) => {
        e.stopPropagation();
        this.currentTags = this.currentTags.filter(t => t !== tag);
        this.syncCheckboxesWithCurrentTags();
        this.updateActiveFilters();
        await this.handleFilterChange();
      });
      this.activeFiltersContainer.appendChild(el);
    });

    // Size badges
    this.currentSizes.forEach(size => {
      const el = document.createElement('div');
      el.className = 'active-filter';
      el.innerHTML = `<span>Size: ${size}</span><button type="button" class="active-filter__remove" data-type="size" data-value="${size}">✕</button>`;
      el.querySelector('button').addEventListener('click', async (e) => {
        e.stopPropagation();
        this.currentSizes = this.currentSizes.filter(s => s !== size);
        document.querySelectorAll('[data-filter-group="size"] .filter-option').forEach(opt => {
          if (opt.dataset.value === size) opt.querySelector('.filter-option__checkbox').classList.remove('checked');
        });
        this.updateActiveFilters();
        await this.handleFilterChange();
      });
      this.activeFiltersContainer.appendChild(el);
    });
  }

  renderUI() {
    this.renderStyleDropdownItems();
    this.renderStyleFilterOptions();
    // Sizes will be loaded asynchronously in loadInitialSizes()
  }

  checkUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const presetTags = params.getAll('tag');
    const presetSizes = params.getAll('size');
    
    if (presetTags.length) {
      this.currentTags = presetTags.map(t => {
        const match = this.availableTags.find(at => this.normalize(at.tag) === this.normalize(t));
        return match ? match.tag : t;
      });
      this.syncCheckboxesWithCurrentTags();
      this.updateActiveFilters();
      this.handleFilterChange();
    } else if (presetSizes.length) {
      this.currentSizes = presetSizes.slice();
      this.updateActiveFilters();
      this.handleFilterChange();
    }
  }




  // Add this method to pre-cache full product data
async preloadFullProductData() {
  try {
    const handles = this.allProducts.map(p => p.handle);
    const fullProducts = await this.fetchFullProductsByHandles(handles);
    
    // Update allProducts with full data
    this.allProducts = fullProducts.map(fp => ({
      id: fp.id,
      title: fp.title,
      price: fp.price,
      handle: fp.handle,
      featured_image: fp.featured_image,
      images: fp.images // Include all images in cache
    }));
    
    console.log('Pre-loaded full product data for fast filtering');
  } catch (error) {
    console.error('Failed to pre-load full product data:', error);
  }
}

// Call this after cacheInitialProducts()
// this.preloadFullProductData();



  // ========== SIMPLIFIED FILTERING - NO BACKGROUND OVERRIDE ==========


  async handleFilterChange() {
  console.log('Filter change triggered:', {
    tags: this.currentTags,
    sizes: this.currentSizes
  });

  // Show loading state immediately
  this.gridContainer.style.opacity = '0.7';
  this.gridContainer.style.pointerEvents = 'none';

  try {
    // No filters -> restore original (instant)
    if ((!this.currentTags || this.currentTags.length === 0) && (!this.currentSizes || this.currentSizes.length === 0)) {
      this.gridContainer.innerHTML = this.originalGridHTML;
      this.rewireClickableCards();
      this.loadInitialSizes();
      return;
    }

    let matchedProducts = [];

    // PHASE 1: Determine which products match the filters
    if (this.currentTags && this.currentTags.length > 0) {
      // Fast client-side filtering
matchedProducts = this.allProducts.filter(product => {
  const productTitle = this.normalize(product.title);
  return this.currentTags.some(tag => 
    productTitle.includes(this.normalize(tag)) || 
    productTitle.includes(this.normalize(this.getTagTitle(tag)))
  );
});
      
      console.log('Fast filtered products:', matchedProducts.length);
      
      // If no instant results, fetch from API
      if (matchedProducts.length === 0) {
        matchedProducts = await this.fetchProductsByTags(this.currentTags);
      }
    } else {
      // No tags selected but sizes selected
      matchedProducts = [...this.allProducts];
    }

    // PHASE 2: Handle no results
    if (!matchedProducts || matchedProducts.length === 0) {
      this.gridContainer.innerHTML = '<div class="no-products"><p>No products found.</p></div>';
      this.renderSizeFilterOptions([]);
      return;
    }

    // PHASE 3: Apply size filtering if needed
    if (this.currentSizes && this.currentSizes.length > 0) {
      // For size filtering, we need full product data
      const fullProducts = await this.fetchFullProductsByHandles(matchedProducts.map(p => p.handle));
      const sizeFilteredProducts = fullProducts.filter(p => this.productHasAnySelectedSize(p));
      
      if (sizeFilteredProducts.length > 0) {
        this.renderProductCards(sizeFilteredProducts);
        this.updateSizeOptions(sizeFilteredProducts);
      } else {
        this.gridContainer.innerHTML = '<div class="no-products"><p>No products found with selected sizes.</p></div>';
        this.renderSizeFilterOptions([]);
      }
    } else {
      // No size filtering needed - fetch full product data for hover images
      const fullProducts = await this.fetchFullProductsByHandles(matchedProducts.map(p => p.handle));
      this.renderProductCards(fullProducts);
      this.updateSizeOptions(fullProducts);
    }

  } catch (error) {
    console.error('Filter error:', error);
    this.gridContainer.innerHTML = '<div class="no-products"><p>Error loading products. Please try again.</p></div>';
  } finally {
    this.gridContainer.style.opacity = '1';
    this.gridContainer.style.pointerEvents = 'auto';
  }
}

  async applySizeFilter(products) {
    try {
      // Fetch full product data for size filtering
      const handles = products.map(p => p.handle);
      const fullProducts = await this.fetchFullProductsByHandles(handles);
      
      // Apply size filter
      const sizeFilteredProducts = fullProducts.filter(p => this.productHasAnySelectedSize(p));
      
      console.log('Size filtered products:', sizeFilteredProducts.length);
      
      if (sizeFilteredProducts.length > 0) {
        this.renderProductCards(sizeFilteredProducts);
        this.updateSizeOptions(sizeFilteredProducts);
      } else {
        this.gridContainer.innerHTML = `<div class="no-products"><p>No products found with selected sizes.</p></div>`;
        this.renderSizeFilterOptions([]);
      }
    } catch (error) {
      console.error('Size filter error:', error);
      // Keep the original filtered results if size filtering fails
    }
  }

  updateSizeOptions(products) {
    if (products && products.length > 0) {
      // For better performance, only fetch sizes from a sample of products
      const sampleProducts = products.slice(0, 6);
      this.fetchSizesForProducts(sampleProducts);
    } else {
      // Show empty sizes when no products
      this.renderSizeFilterOptions([]);
    }
  }

  async fetchSizesForProducts(products) {
    try {
      const handles = products.map(p => p.handle);
      const fullProducts = await this.fetchFullProductsByHandles(handles);
      const dynamicSizes = this.extractSizesFromProducts(fullProducts);
      this.renderSizeFilterOptions(dynamicSizes);
    } catch (error) {
      console.error('Error fetching sizes:', error);
      // Show placeholder sizes on error
      const placeholderSizes = ['26', '28', '30', '32', '34', '36', '38'];
      this.renderSizeFilterOptions(placeholderSizes);
    }
  }

  getTagTitle(tag) {
    const found = this.availableTags.find(t => t.tag === tag);
    return found ? found.title : tag;
  }

  // Optimized product fetching
  async fetchProductsByTags(tags) {
    if (!tags || tags.length === 0) return [];
    
    const cacheKey = tags.sort().join('|');
    
    // Check cache first
    if (this.searchCache.has(cacheKey)) {
      const cachedHandles = this.searchCache.get(cacheKey);
      return this.fetchFullProductsByHandles(cachedHandles);
    }

    // Use search.json API which is much faster than HTML parsing
    const searchPromises = tags.map(async (tag) => {
      const query = this.buildSearchQueryForTag(tag);
      try {
        const url = `/search.json?q=${encodeURIComponent(query)}&type=product&limit=50`;
        const resp = await fetch(url);
        if (!resp.ok) return [];
        
        const data = await resp.json();
        return data.products || [];
      } catch (err) {
        console.warn(`Failed to search for tag: ${tag}`, err);
        return [];
      }
    });

    const results = await Promise.all(searchPromises);
    const allProducts = results.flat();
    
    // Cache the handles for future use
    const handles = allProducts.map(p => p.handle).filter(Boolean);
    this.searchCache.set(cacheKey, handles);
    
    return allProducts;
  }

  buildSearchQueryForTag(tag) {
    const t = String(tag).trim();
    return /\s/.test(t) ? `tag:"${t}"` : `tag:${t}`;
  }

  async fetchFullProductsByHandles(handles) {
    if (!handles || handles.length === 0) return [];
    const results = [];
    const seenIds = new Set();

    // Use smaller batch size for faster response
    const CONC = 4;
    for (let i = 0; i < handles.length; i += CONC) {
      const batch = handles.slice(i, i + CONC);
      const promises = batch.map(h => 
        fetch(`/products/${h}.js`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      );
      const batchRes = await Promise.all(promises);
      batchRes.forEach(prod => {
        if (prod && prod.id && !seenIds.has(prod.id)) {
          seenIds.add(prod.id);
          results.push(prod);
        }
      });
    }
    return results;
  }

  extractSizesFromProducts(products) {
    const sizes = new Set();

    products.forEach(p => {
      if (!p || !p.variants) return;

      p.variants.forEach(v => {
        [v.option1, v.option2, v.option3].forEach(opt => {
          if (!opt) return;

          const value = String(opt).trim();
          if (/^\d+$/.test(value)) {
            sizes.add(value);
          }
        });
      });
    });

    return Array.from(sizes).sort((a, b) => Number(a) - Number(b));
  }

  productHasAnySelectedSize(product) {
    if (!this.currentSizes || this.currentSizes.length === 0) return true;
    if (!product || !product.variants) return false;
    
    return product.variants.some(v => {
      return [v.option1, v.option2, v.option3].some(opt => {
        if (!opt) return false;
        const val = String(opt).trim();
        return this.currentSizes.some(sel => this.normalize(val) === this.normalize(sel));
      });
    });
  }

  buildProductCardHTML(product) {
    const images = Array.isArray(product.images) ? product.images : [];
    const img0 = images[0] || product.featured_image || '';
    const img1 = images[1] || '';
const price = product.price 
  ? (product.price / 100).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }) 
  : '';
    const handle = product.handle;
    const title = product.title || '';

    return `
      <div class="ai-product-card-${this.aiGenId}" data-product-id="${product.id}">
        <div class="ai-product-card__image-wrapper-${this.aiGenId}">
          ${img0 ? `<img src="${img0}" alt="${this.escapeHtml(title)}" class="ai-product-card__image-${this.aiGenId} ai-product-card__image-primary-${this.aiGenId}" loading="lazy" width="600" height="600">` : `<div class="ai-product-card__image-placeholder-${this.aiGenId}"></div>`}
          ${img1 ? `<img src="${img1}" alt="${this.escapeHtml(title)}" class="ai-product-card__image-${this.aiGenId} ai-product-card__image-secondary-${this.aiGenId}" loading="lazy" width="600" height="600">` : ''}
        </div>

        <div class="ai-product-card__info-${this.aiGenId}">
          <div class="ai-product-card__details-${this.aiGenId}">
            <h3 class="ai-product-card__title-${this.aiGenId}">
              <a href="/products/${handle}">${this.escapeHtml(title)}</a>
            </h3>
            <div class="ai-product-card__price-${this.aiGenId}">
              ${price}
            </div>
          </div>
          <button
            class="ai-product-card__quick-add-${this.aiGenId}"
            data-quick-add
            data-product-handle="${handle}"
            aria-label="Quick add ${this.escapeHtml(title)}"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        </div>

        <div class="ai-product-card__overlay-${this.aiGenId}" data-overlay>
          <button class="ai-product-overlay__close-${this.aiGenId}" aria-label="Close overlay">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div class="ai-overlay__title-${this.aiGenId}">Select size</div>
          <div class="ai-overlay__sizes-${this.aiGenId}" data-sizes></div>
          <button class="ai-overlay__button-${this.aiGenId}" data-add-button>
            Select Size
          </button>
<div class="ai-add-to-cart-container-${this.aiGenId} hide" data-product-handle="${handle}">
  <product-form>
    <form method="post" action="/cart/add" class="cart-form">
      <input type="hidden" name="id" value="" data-variant-input>
      <button type="submit" class="product-form__submit button button--full-width button--primary">
        Add to cart
      </button>
    </form>
  </product-form>
</div>
        </div>
      </div>
    `;
  }

  renderProductCards(products) {
    if (!products || products.length === 0) {
      this.gridContainer.innerHTML = `<div class="no-products"><p>No products found.</p></div>`;
      return;
    }
    const html = products.map(p => this.buildProductCardHTML(p)).join('');
    this.gridContainer.innerHTML = html;
    this.rewireClickableCards();
    
    try {
      this.productGridEl && this.productGridEl.dispatchEvent(new CustomEvent('ai:products:updated', { bubbles: true }));
    } catch (e) { 
      console.error('Error dispatching event:', e);
    }
  }

  rewireClickableCards() {
    const cards = Array.from(this.gridContainer.querySelectorAll(`.ai-product-card-${this.aiGenId}`));
    cards.forEach(card => {
      const clone = card.cloneNode(true);
      if (card.parentNode) {
        card.parentNode.replaceChild(clone, card);
      }
    });

    const freshCards = Array.from(this.gridContainer.querySelectorAll(`.ai-product-card-${this.aiGenId}`));
    freshCards.forEach(card => {
      const linkEl = card.querySelector(`.ai-product-card__title-${this.aiGenId} a`);
      if (!linkEl) return;
      const productUrl = linkEl.getAttribute('href');
      card.addEventListener('click', (e) => {
        if (e.target.closest('button') || e.target.closest('[data-overlay]') || e.target.closest(`.ai-product-card__quick-add-${this.aiGenId}`)) return;
        window.location.href = productUrl;
      });
    });
  }

  clearAllFilters() {
    this.currentTags = [];
    this.currentSizes = [];
    document.querySelectorAll('.filter-option__checkbox.checked').forEach(cb => cb.classList.remove('checked'));
    document.querySelectorAll('.style-dropdown__item.active').forEach(b => b.classList.remove('active'));
    this.updateActiveFilters();
    this.gridContainer.innerHTML = this.originalGridHTML;
    this.rewireClickableCards();
    this.closeFilterDrawer();
    
    // Reload initial sizes when clearing filters
    this.loadInitialSizes();
  }
}

window.FilterSystem = FilterSystem;
// Make FilterSystem available globally